export type Payment = {
    uuid: string;
    amount: string;
    payee: string;
    payer: string;
    payment_type: string;
    paid_at: string;
    deleted_at: string | null;
    payment_receipt: string | null;
    quarter: 1 | 2 | 3 | 4;
    year: number;
    member?: string;
    created_at: string;
};

export type BreakdownItem = {
    name: string;
    amount: string | null;
    annualized_amount: string;
};

export enum IncomeType {
    TEN_NINETY_NINE = 'TEN_NINETY_NINE',
    SHORT_TERM_CAPITAL_GAIN = 'SHORT_TERM_CAPITAL_GAIN',
    LONG_TERM_CAPITAL_GAIN = 'LONG_TERM_CAPITAL_GAIN',
}

export type ManualInputs = {
    net_income: string;
    outside_income: string;
    s_corp_taxes_due: string;
    individual_taxes_due: string;
    due_this_quarter: string;
    prior_year_state_tax_liability?: string;
    agi?: string;
    estimation_method?: string;
    // Additional properties for breakdown modal
    businessIncome?: string;
    businessExpenses?: string;
    w2Income?: string;
    federalW2Withholding?: string;
    stateW2Withholding?: string;
    annualized_federal_liability?: string;
    annualized_state_liability?: string;
    federalTax?: {
        businessMarginalTaxRate?: string;
        w2MarginalTaxRate?: string;
        effectiveTaxRateBeforeWithholding?: string;
    };
    stateTax?: {
        businessMarginalTaxRate?: string;
        w2MarginalTaxRate?: string;
        effectiveTaxRateBeforeWithholding?: string;
    };
    otherIncomes?: Array<{
        type: IncomeType;
        amount: number;
    }>;
    w2_withholdings?: {
        federal_business_w2_withholding: string;
        state_business_w2_withholding: string;
        federal_household_w2_withholding: string;
        state_household_w2_withholding: string;
        total_federal_w2_withholding: string;
        total_state_w2_withholding: string;
    };
    business_w2_income?: string;
    household_w2_income?: string;
    state_safe_harbor_percentage?: string;
    previous_state_payments_total?: string;
    schedule_c_net_income?: string;
};

export interface FederalManualInputs extends ManualInputs {
    marginal_tax_rate?: string;
    effective_tax_rate?: string;
    capital_gain_tax_rate?: string;
    projected_annualized_income?: string;
    projected_annualized_tax_liability?: string;
    prior_year_federal_tax_liability?: string;
    federal_safe_harbor_percentage?: string;
    previous_federal_payments_total?: string;
}

export type FederalBreakdown = {
    incomes: BreakdownItem[];
    adjustments: BreakdownItem[];
    deductions: BreakdownItem[];
    tax_rates: BreakdownItem[];
    credits: BreakdownItem[];
    taxable_income: BreakdownItem;
    total_income: BreakdownItem;
    total_annualized_tax_liability: BreakdownItem;
    outside_amounts: BreakdownItem[];
    outstanding_tax_liability: BreakdownItem;
    due_this_quarter: BreakdownItem;
    manual_inputs?: FederalManualInputs;
};

export type StateBreakdown = {
    sourced_to_state: BreakdownItem;
    incomes: BreakdownItem[];
    adjustments: BreakdownItem[];
    deductions: BreakdownItem[];
    tax_rates: BreakdownItem[];
    credits: BreakdownItem[];
    taxable_income: BreakdownItem;
    total_income: BreakdownItem;
    total_annualized_tax_liability: BreakdownItem;
    outside_amounts: BreakdownItem[];
    outstanding_tax_liability: BreakdownItem;
    due_this_quarter: BreakdownItem;
    manual_inputs?: ManualInputs;
};

type StateEstimations = {
    state: string;
    total_amount: string;
    amount: string;
    outside_amount: string | null;
    breakdown: StateBreakdown | undefined;
};

type FederalEstimation = {
    amount: string;
    outside_amount: string | null;
    total_amount: string;
    breakdown: FederalBreakdown | undefined;
};

type BusinessEstimations = {
    state: string;
    tax_name: string;
    amount: string;
    /**
     * Business estimations expose the raw calculator payload rather than the derived `breakdown`
     * object that federal and state estimations get.
     */
    calculator_data?: Record<string, string>;
};

type PreviousQTE = {
    uuid: string;
    quarter: number;
    year: number;
    total_state: string;
    total_federal: string;
    total: string;
};

type QTEComment = {
    uuid: string;
    content: string;
};

export type QTE = {
    uuid: string;
    tax_id: string;
    net_income: string;
    outside_income: string | null;
    gross_wages: string;
    status: string;
    is_manual: boolean;
    federal_estimation: FederalEstimation | null;
    state_estimations: StateEstimations[];
    /**
     * Named to match the API. This was previously declared as `business_estimations`, a key
     * `/qte/member-qte/` has never sent, and it went unnoticed because every reference to it was a
     * test mock — no production code read it until the Fees and Franchise Taxes tab.
     */
    business_tax_estimations: BusinessEstimations[];
    quarter: 1 | 2 | 3 | 4;
    year: number;
    estimated_federal_tax: string;
    estimated_state_tax: string;
    estimated_business_tax: string;
    total_estimated: string;
    previous_qte: PreviousQTE | null;
    comments: QTEComment[];
    /** When the estimate was last computed — null for a never-estimated QTE */
    calculated_at: string | null;
};

export enum QTEStatus {
    NOT_STARTED = 'not_started',
    IN_PROGRESS = 'in_progress',
    ESTIMATED = 'estimated',
    ESTIMATE_COMPLETED = 'estimate_completed',
    COMPLETED = 'completed',
}

export enum QTEType {
    FEDERAL = 'federal',
    STATE = 'state',
    BUSINESS = 'business',
}

type StateOfResidency = {
    id: string;
    name: string;
};

type ForeignRegistration = {
    uuid: string;
    state: string;
    entity_id: string;
    entity_name: string;
    effective_registration_date: string;
};

type HomeRegionHistory = {
    region: StateOfResidency;
    move_effective_date: string;
};

type BookkeepingConnection = {
    uuid: string;
    service: string;
    status: string;
    service_id: string;
    service_name: string;
    user: number;
};

type State = {
    id: string;
    name: string;
};

export type Nexus = {
    id: number;
    state: State;
    state_name?: string;
    percentage?: number | null;
    amount?: string | null;
    start_year: number;
    end_year?: number;
    quarter_notified: 1 | 2 | 3 | 4;
};

type GustoConnection = {
    uuid: string;
    service: string;
    status: string;
    service_id: string;
    service_name: string;
    user: number;
};

export type TaxProfile = {
    id: number;
    state_filing_status: string;
    federal_filing_status: string;
    nexus: Nexus[];
    calculation_type: string;
    state_of_residency: StateOfResidency;
    foreign_registrations: ForeignRegistration[];
    home_region_history: HomeRegionHistory[];
    bookkeeping_connection: BookkeepingConnection;
    gusto_connection: GustoConnection;

    financials_status?: string;
    questionnaire_status?: string;
    updated_at?: string;
};

interface StateFallbackResidencyInfo {
    form: string;
    residentStatus: string;
    amountDueLineRef: string;
    refundAmountLineRef: string;
}

export interface StateFallbackInfo {
    description: string;
    payTaxesDueUrl: string;
    trackRefundsUrl: string;
    resident: StateFallbackResidencyInfo;
    nonResident?: StateFallbackResidencyInfo;
}
